// CTSK-146 (FR #299) — project session references, web surfaces.
//
// Two components:
//
//   · SessionRefsSection — the read surface ("Sessions" section on
//     Project Detail). Renders records newest-first with kind DERIVED
//     AT RENDER TIME (classifyProjectSessionRef — no kind enum is
//     stored):
//       session_key → link to the greet intro doc when resolvable
//                     (GET /introductions/resolve, CTSK-210); plain
//                     mono display otherwise
//       slack       → link out
//       url         → link out
//       opaque      → display only + copy-to-clipboard
//
//   · SessionRefsEditor — the authoring surface inside the Edit
//     Project dialog's "More fields" expand (ctsk-137 parity). Local
//     list edits only; the dialog PATCHes the full array on Save
//     (operator-only replace — producers append via the API instead).
//
// Provenance note: `added_at` / `added_by_source_tag` are server-
// stamped. The editor round-trips existing records verbatim and sends
// new entries as {ref, label} — the server ignores client provenance.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ProjectSessionRef } from '@captain-tasko/shared';
import {
  classifyProjectSessionRef,
  validateProjectSessionRefs,
  PROJECT_SESSION_REFS_MAX_COUNT,
  type ProjectSessionRefKind,
} from '@captain-tasko/shared';
import { apiSingle } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { Copy, ExternalLink, MessageSquare, Terminal, X } from 'lucide-react';

/** Editor rows are stored records (round-tripped) or fresh adds. */
export interface SessionRefDraft {
  ref: string;
  label?: string;
  added_at?: string;
  added_by_source_tag?: string;
}

interface ResolveResult {
  sessionKey: string;
  documentId: string | null;
  /** CTSK-401 — which kind of doc `documentId` points at. Drives the
   *  tooltip differentiation below; null means neither an introduction
   *  nor an access-request doc exists (plain display, no link). */
  docKind: 'introduction' | 'access_request' | null;
  greetId: string | null;
  friendlyName: string | null;
}

const KIND_LABEL: Record<ProjectSessionRefKind, string> = {
  session_key: 'Cowork session',
  slack: 'Slack thread',
  url: 'Link',
  opaque: 'Reference',
};

function KindChip({ kind }: { kind: ProjectSessionRefKind }) {
  return (
    <span
      style={{
        fontSize: 11,
        padding: '1px 6px',
        borderRadius: 8,
        background: 'color-mix(in srgb, var(--accent-primary) 10%, transparent)',
        color: 'var(--accent-primary)',
        whiteSpace: 'nowrap',
      }}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

function CopyButton({ value }: { value: string }) {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm"
      title="Copy to clipboard"
      aria-label={`Copy ${value}`}
      style={{ padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}
      onClick={() => {
        // CTSK-281 — shared wrapper handles the insecure-origin (plain
        // HTTP LAN) fallback where navigator.clipboard is unavailable.
        void copyToClipboard(value).then((ok) =>
          ok ? toast.success('Copied') : toast.error('Copy failed'),
        );
      }}
    >
      <Copy size={12} strokeWidth={1.75} aria-hidden />
    </button>
  );
}

/** One rendered record on the read surface. */
function SessionRefItem({
  record,
  onNavigate,
}: {
  record: ProjectSessionRef;
  onNavigate?: (path: string) => void;
}) {
  const kind = classifyProjectSessionRef(record.ref);

  // session_key → greet intro doc lookup (CTSK-210). Only fires for
  // session_key kinds; a null documentId is a normal state (session
  // never greeted), rendered as plain display.
  const resolve = useQuery({
    queryKey: ['introduction-resolve', record.ref],
    queryFn: () =>
      apiSingle<ResolveResult>(
        `/introductions/resolve?sessionKey=${encodeURIComponent(record.ref)}`,
      ),
    enabled: kind === 'session_key',
    staleTime: 5 * 60_000,
    retry: false,
  });

  const added = new Date(record.added_at);
  const meta = (
    <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
      added {isNaN(added.getTime()) ? record.added_at : added.toLocaleDateString()} by{' '}
      <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {record.added_by_source_tag}
      </code>
    </span>
  );

  // CTSK-281 — when a label is set, BOTH label and ref stay visible
  // (no info hidden behind the title tooltip), and every branch keeps
  // a copy button for the raw ref value.
  const labelSubtitle = record.label ? (
    <span
      style={{
        display: 'block',
        fontSize: 11,
        color: 'var(--text-muted)',
        overflowWrap: 'anywhere',
      }}
    >
      {record.label}
    </span>
  ) : null;

  let body: React.ReactNode;
  if (kind === 'session_key') {
    const doc = resolve.data ?? null;
    const mono = (
      <code
        title={record.ref}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          overflowWrap: 'anywhere',
        }}
      >
        {record.ref}
      </code>
    );
    // CTSK-401 — clickable for either docKind (introduction or the
    // access-request fallback); the tooltip differentiates which kind
    // of provenance doc the link resolves to. null docKind (neither
    // exists) stays plain display, same as pre-fix behavior.
    body = (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {doc?.documentId && doc.docKind && onNavigate ? (
            <a
              href={`/documents/${doc.documentId}`}
              title={
                doc.docKind === 'introduction'
                  ? doc.friendlyName
                    ? `Greet introduction: ${doc.friendlyName}`
                    : 'Greet introduction document'
                  : doc.friendlyName
                    ? `Access request: ${doc.friendlyName}`
                    : 'Access request document'
              }
              onClick={(e) => {
                e.preventDefault();
                onNavigate(`/documents/${doc.documentId}`);
              }}
              style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}
            >
              {mono}
            </a>
          ) : (
            mono
          )}
          <CopyButton value={record.ref} />
        </span>
        {labelSubtitle}
      </span>
    );
  } else if (kind === 'slack' || kind === 'url') {
    body = (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <a
            href={record.ref}
            target="_blank"
            rel="noopener noreferrer"
            title={record.ref}
            style={{
              color: 'var(--accent-primary)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              overflowWrap: 'anywhere',
            }}
          >
            <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
            {record.label ?? record.ref}
          </a>
          <CopyButton value={record.ref} />
        </span>
        {/* When a label overrides the link text, surface where the
            link goes without requiring a hover. */}
        {record.label && (
          <code
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-muted)',
              overflowWrap: 'anywhere',
            }}
          >
            {record.ref}
          </code>
        )}
      </span>
    );
  } else {
    body = (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {record.label && <span style={{ fontSize: 12 }}>{record.label} —</span>}
        <code
          title={record.ref}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 12, overflowWrap: 'anywhere' }}
        >
          {record.ref}
        </code>
        <CopyButton value={record.ref} />
      </span>
    );
  }

  const icon =
    kind === 'session_key' ? (
      <Terminal size={13} strokeWidth={1.75} aria-hidden style={{ color: 'var(--text-muted)' }} />
    ) : kind === 'slack' ? (
      <MessageSquare
        size={13}
        strokeWidth={1.75}
        aria-hidden
        style={{ color: 'var(--text-muted)' }}
      />
    ) : null;

  return (
    <li
      data-kind={kind}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      {icon}
      <span style={{ flex: 1, minWidth: 0 }}>{body}</span>
      <KindChip kind={kind} />
      {meta}
    </li>
  );
}

/**
 * "Sessions" section body — Project Detail read surface. Ordering:
 * `added_at` desc (newest first) per the ratified design.
 */
export function SessionRefsSection({
  refs,
  onNavigate,
}: {
  refs: ProjectSessionRef[];
  onNavigate?: (path: string) => void;
}) {
  if (!refs.length) {
    return (
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
        No session references yet. Producer sessions can self-append via the API; operators can
        add refs from Edit project → More fields.
      </p>
    );
  }
  const ordered = [...refs].sort((a, b) => b.added_at.localeCompare(a.added_at));
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {ordered.map((r) => (
        <SessionRefItem key={r.ref} record={r} onNavigate={onNavigate} />
      ))}
    </ul>
  );
}

/**
 * Editor for the Edit Project dialog (operator full-replace surface).
 * Pure controlled component — owns no server state.
 */
export function SessionRefsEditor({
  value,
  onChange,
}: {
  value: SessionRefDraft[];
  onChange: (next: SessionRefDraft[]) => void;
}) {
  const [newRef, setNewRef] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const trimmed = newRef.trim();
  // Early shape hint (authoritative validation is server-side).
  const addIssues = trimmed
    ? validateProjectSessionRefs([...value.map((v) => v.ref), trimmed]).filter(
        (i) => i.index === value.length || i.issue === 'too_many_refs',
      )
    : [];
  const addError = addIssues[0]?.issue ?? null;
  const atCap = value.length >= PROJECT_SESSION_REFS_MAX_COUNT;

  function add() {
    if (!trimmed || addError) return;
    const label = newLabel.trim();
    onChange([...value, { ref: trimmed, ...(label ? { label } : {}) }]);
    setNewRef('');
    setNewLabel('');
  }

  return (
    <div>
      {value.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0 }}>
          {value.map((r, idx) => (
            <li
              key={r.ref}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}
            >
              <code
                title={r.ref}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  flex: 1,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {r.label ? `${r.label} — ${r.ref}` : r.ref}
              </code>
              <KindChip kind={classifyProjectSessionRef(r.ref)} />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-label={`Remove session reference ${r.ref}`}
                title="Remove (applies on Save)"
                style={{ padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}
                onClick={() => onChange(value.filter((_, i) => i !== idx))}
              >
                <X size={12} strokeWidth={1.75} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <input
          aria-label="New session reference"
          placeholder="e.g. local_8e04b81b-756f-42a0-9ce6-0c1029c684c2 / https://…slack.com/archives/… / hyper-scanno-run-2026-07-30-001"
          value={newRef}
          onChange={(e) => setNewRef(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          disabled={atCap}
          aria-invalid={!!addError}
          aria-describedby={addError ? 'session-ref-add-err' : undefined}
          style={{ flex: 2, minWidth: 0 }}
        />
        <input
          aria-label="Label (optional)"
          placeholder="Label (optional)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          disabled={atCap}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          className="btn btn--sm"
          onClick={add}
          disabled={!trimmed || !!addError || atCap}
        >
          Add
        </button>
      </div>
      {/* CTSK-281 — nudge against the field-name-as-value mistake
          (e.g. entering `sessionKey` / `sourceTag` literally). */}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
        Use a real session id, Slack URL, or opaque producer identifier — not a column name like{' '}
        <code>sessionKey</code> or <code>sourceTag</code>.
      </div>
      {addError && (
        <div
          id="session-ref-add-err"
          style={{ fontSize: 12, color: 'var(--danger-primary)', marginTop: 4 }}
        >
          {addError === 'invalid_session_key_ref' &&
            'Looks like a session key but doesn’t match the canonical local_… / cse_… grammar.'}
          {addError === 'invalid_slack_ref' &&
            'Looks like a Slack link but isn’t a message/thread URL (…slack.com/archives/…).'}
          {addError === 'duplicate_ref' && 'This reference is already on the project.'}
          {addError === 'ref_too_long' && 'References are capped at 512 characters.'}
          {addError === 'too_many_refs' &&
            `A project holds at most ${PROJECT_SESSION_REFS_MAX_COUNT} session references.`}
          {addError === 'ref_empty' && 'Reference cannot be empty.'}
        </div>
      )}
      {atCap && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Reference cap reached ({PROJECT_SESSION_REFS_MAX_COUNT}).
        </div>
      )}
    </div>
  );
}
